import { PartnerLayout } from 'features/partners/PartnerLayout/PartnerLayout.component';
import { GetStaticPaths } from 'next';
import Error from 'pages/_error';
import { Content, Partner } from 'pleinchamp-api-client';
import { createContext } from 'react';
import { usePlcApiCall } from '@api/business/api.hook';
import { fetchLastActus } from '@api/business/api.utils';
import { FullPageLoader } from '@layout/components/FullPageLoader/FullPageLoader.component';
import { ApiStatusCode, formatPropsForStatic } from '@utils/api.utils';
import { usePlcRouter } from '@utils/customHooks';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString } from '@utils/url';
import { PlcNextPage, PlcNextPageContext, PlcPageContextProps } from 'features/business/app.types';
import { getPartners, PartnerApi } from 'features/partners/business/partners.utils';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

const NB_LAST_ACTUS = 2;

interface Props {
  slug: string;
  partner: Partner;
  lastActus: Content[];
}

async function fetchPartner(slug: Props['slug']) {
  const r = await PartnerApi.getPartnerPage(slug);
  return r.data;
}

export const PartnerContext = createContext<PlcPageContextProps<Props>>({
  lastActus: { isValidating: false },
  partner: { isValidating: false },
  slug: { isValidating: false },
});

const PartnerPage: PlcNextPage<Partial<Props>> = (props) => {
  const { slug: slugProps, partner: partnerProps } = props;
  const slug = { data: slugProps, isValidating: false };
  const partner = usePlcApiCall(fetchPartner, [slugProps || ''], {
    initialData: partnerProps,
  });
  const lastActus = usePlcApiCall(fetchLastActus, [NB_LAST_ACTUS]);

  const { isFallback } = usePlcRouter();
  if (isFallback) {
    return <FullPageLoader visible={true} />;
  }
  if (partner.error?.response?.status === ApiStatusCode.notFound) {
    return <Error errorCode={ApiStatusCode.notFound} />;
  }
  return (
    <PartnerContext.Provider
      value={{
        lastActus,
        partner,
        slug,
      }}
    >
      <PartnerLayout />
    </PartnerContext.Provider>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  let partners: Partner[] = [];
  try {
    partners = await getPartners();
  } catch {
    partners = [];
  }
  const paths = partners.map((p) => ({
    params: { slug: p.slug },
  }));
  return {
    fallback: true,
    paths,
  };
};

export async function getStaticProps({ locale, params }: PlcNextPageContext) {
  const slug = castUrlParamToString(params?.slug) as string;

  try {
    const partner = await fetchPartner(slug);
    return formatPropsForStatic({
      ...(await serverSideTranslations(locale || 'fr', [...defaultNamespaces, 'partners'])),
      partner,
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return formatPropsForStatic(await serverSideTranslations(locale || 'fr', [...defaultNamespaces, 'partners']));
  }
}

export default PartnerPage;
